import { ModalBody, ModalFooter, ModalHeader } from "@nextui-org/react";
import InputSelect, { SelectItemType } from "../../../InputSelect/InputSelect";
import Button from "../../../Button/Button";
import { useAppDispatch, useAppSelector } from "../../../../store/hooks";
import {
  resetFields,
  updateAction,
  updateAssessmentData,
  updateSelectedAccount,
} from "../../../../store/slices/unifiedBalanceSlice";
import { colors } from "../../../../styles/_variables";
import { useEffect, useState } from "react";
import { getAllAssessmentAccounts } from "../../../../connectors/unifiedBalanceApi";
import { useQuery } from "@tanstack/react-query";
import { genericErrorMessage, handleTooltipsText } from "../../../../utils";
import { AssessmentAccount } from "../../types/types";
import { useLocation } from "react-router-dom";
import { addMessage } from "../../../../utils/addMessage";
import Spinner from "../../../Spinner/Spinner";
import styles from "./AccountSelector.module.scss";
import useScreenSize from "../../../../hooks/useScreenSize";
import OptionsBottomSheet from "../../../OptionsBottomSheet/OptionsBottomSheet";

const AccountSelector = () => {
  const {
    spinnerContainer,
    modalHeader,
    modalFooterMobile,
    bottomSheetOption,
    modalMobileHeader,
  } = styles;
  const [isBottomSheetOpen, setIsBottomSheetOpen] = useState(false);
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);

  const dispatch = useAppDispatch();

  const {
    assessmentData: { assessment },
  } = useAppSelector((state) => state.unifiedBalance);
  const { user } = useAppSelector((state) => state.authentication);
  const { isMobile } = useScreenSize();

  const email = queryParams.get("email") || user?.email || "";

  const [selectedAccount, setSelectedAccount] = useState<SelectItemType>();
  const [accounts, setAccounts] = useState<AssessmentAccount[]>();

  //Get allaria accounts
  const {
    data: assessmentAccounts,
    isLoading: assessmentAccountsLoading,
    isFetching: assessmentAccountsFetching,
    error: assessmentAccountsError,
  } = useQuery({
    // enabled: !!email,
    enabled: false,
    queryFn: () =>
      getAllAssessmentAccounts({
        email,
      }),
    queryKey: ["assessmentAccounts", email],
    refetchInterval: false,
    refetchOnWindowFocus: false,
    // staleTime: 5 * 60 * 1000,
  });

  // Handle error
  useEffect(() => {
    if (assessmentAccountsError) {
      dispatch(updateAction("INITIAL"));
      addMessage({
        messageId: "allariaMasModal",
        message: genericErrorMessage,
        type: "error",
        position: "top-right",
      });
    }
  }, [assessmentAccountsError]);

  useEffect(() => {
    if (assessmentAccounts && !assessmentAccountsLoading)
      setAccounts(assessmentAccounts);
  }, [assessmentAccounts, assessmentAccountsLoading]);

  useEffect(() => {
    if (assessmentAccountsLoading) return;

    const allAccountsUnified = assessmentAccounts?.every(
      (acc: AssessmentAccount) =>
        !acc.assessment.is_allowed && acc.assessment.unification_status,
    );

    if (allAccountsUnified) {
      dispatch(updateAction("UNIFIED_ACCOUNTS_DATA"));
    }
  }, [assessmentAccounts, assessmentAccountsLoading]);

  // Select account if only one account is available
  useEffect(() => {
    if (!accounts) return;

    if (
      accounts?.length === 1 &&
      (accounts[0]?.assessment.is_allowed ||
        (!accounts[0]?.assessment.is_allowed &&
          accounts[0]?.assessment.failed_reason ===
            "ALLARIAMAS_ACCOUNT_NOT_EXISTS"))
    ) {
      const {
        allaria_account: { denomination, number },
      } = accounts[0];
      setSelectedAccount({
        label: `${denomination} - ${number}`,
        value: String(number),
      });
    }
  }, [accounts]);

  // Update selected account in redux store
  useEffect(() => {
    if (selectedAccount) {
      dispatch(
        updateSelectedAccount({
          name: selectedAccount.label,
          number: selectedAccount.value,
        }),
      );

      const account = accounts
        ?.filter(
          ({ assessment }) =>
            assessment.is_allowed ||
            (!assessment.is_allowed &&
              assessment.failed_reason === "ALLARIAMAS_ACCOUNT_NOT_EXISTS"),
        )
        .find(
          ({ allaria_account: { number } }) =>
            selectedAccount.value === String(number),
        );

      dispatch(
        updateAssessmentData({
          assessment: account?.assessment || {},
          mainHolders: account?.main_holders || [],
          availablesAllariaMasAccounts: account?.allaria_mas_accounts || [],
          allaria_account: account?.allaria_account || {},
        }),
      );
    } else {
      dispatch(
        updateSelectedAccount({
          name: "",
          number: "",
        }),
      );
      dispatch(resetFields());
    }
  }, [selectedAccount]);

  useEffect(() => {
    if (!isMobile) return;
    const closeBtn = document.querySelector(
      '[aria-label="Close"]',
    ) as HTMLElement | null;
    if (!closeBtn) return;
    closeBtn.style.visibility =
      assessmentAccountsLoading || assessmentAccountsFetching
        ? "hidden"
        : "visible";
  }, [assessmentAccountsLoading, assessmentAccountsFetching, isMobile]);

  // InputSelect options
  const parsedAccounts = accounts
    ?.filter((acc) => !acc.assessment.unification_status)
    .map(
      ({
        allaria_account: { denomination, number },
        assessment: { required_action },
      }) => ({
        label: `${denomination} - ${number}${
          required_action === "TO_BE_DEFINED" ? " - TO_BE_DEFINED" : ""
        }`,
        value: String(number),
      }),
    );

  // InputSelect disabled options
  const getDisabledOptions = () => {
    const isDisabled = accounts
      ?.map(({ assessment, allaria_account }) => {
        if (
          (!assessment.is_allowed &&
            allaria_account &&
            assessment.failed_reason !== "ALLARIAMAS_ACCOUNT_NOT_EXISTS") ||
          (assessment.is_allowed &&
            assessment.required_action === "TO_BE_DEFINED")
        )
          return String(allaria_account.number);
        else return "";
      })
      .filter((option) => option); // Filter out empty strings

    return isDisabled;
  };

  // InputSelect tooltips
  const getTooltips = () => {
    const tooltips = accounts?.reduce(
      (acc, { assessment, allaria_account }) => {
        if (
          allaria_account &&
          !assessment.is_allowed &&
          assessment.failed_reason !== "ALLARIAMAS_ACCOUNT_NOT_EXISTS"
        ) {
          const tooltipText = handleTooltipsText(assessment.failed_reason);
          if (tooltipText) {
            acc[allaria_account.number] = tooltipText;
          }
        }
        return acc;
      },
      {} as Record<string, JSX.Element>,
    );

    return tooltips;
  };

  return (
    <>
      {(assessmentAccountsLoading || assessmentAccountsFetching) && (
        <div className={spinnerContainer}>
          <Spinner />
          <p>Cargando...</p>
          <p>En instantes vas a poder unificar tus cuentas.</p>
        </div>
      )}

      {!(assessmentAccountsLoading || assessmentAccountsFetching) && (
        <>
          <ModalHeader className={modalMobileHeader}>
            {!isMobile ? (
              <h1
                style={{
                  color: colors.primary[500],
                  fontSize: "1.5rem",
                  fontWeight: 600,
                }}
              >
                Unificar saldos
              </h1>
            ) : (
              <h1 className={modalHeader}>Unificar saldos</h1>
            )}
          </ModalHeader>
          <ModalBody>
            <div className={isMobile ? "relative" : ""}>
              <InputSelect
                disabledOptions={getDisabledOptions()}
                optionsTooltip={getTooltips()}
                isDisabled={
                  assessmentAccountsLoading || assessmentAccountsFetching
                }
                isLoading={
                  assessmentAccountsLoading || assessmentAccountsFetching
                }
                label="CUENTA COMITENTE A UNIFICAR"
                placeholder="Seleccionar cuenta comitente"
                type="single"
                items={parsedAccounts || []}
                inputValue={selectedAccount?.value}
                onChange={(value) => {
                  const account = parsedAccounts?.find(
                    ({ value: accountValue }) => accountValue === value,
                  );
                  setSelectedAccount(account);
                }}
              />
              {isMobile && (
                <div
                  className="absolute inset-0 cursor-pointer"
                  onClick={() => setIsBottomSheetOpen(true)}
                />
              )}
            </div>
            {isMobile && (
              <OptionsBottomSheet
                open={isBottomSheetOpen}
                onClose={() => setIsBottomSheetOpen(false)}
                title="Cuenta comitente a unificar"
                value={selectedAccount?.value}
                classNames={{ label: bottomSheetOption }}
                options={(parsedAccounts || []).map((account) => ({
                  label: account.label,
                  key: account.value,
                  disabled: getDisabledOptions()?.includes(account.value),
                  onClick: () => setSelectedAccount(account),
                }))}
              />
            )}
          </ModalBody>
          <ModalFooter
            className={
              !isMobile ? "flex justify-center items-center" : modalFooterMobile
            }
          >
            <div className={isMobile ? modalFooterMobile : "flex gap-4"}>
              <Button
                variant="secondary"
                text="Volver"
                onPress={() => dispatch(updateAction("INITIAL"))}
                className={isMobile ? "w-full mb-2.5" : ""}
              />
              <Button
                isDisabled={
                  assessmentAccountsLoading ||
                  assessmentAccountsFetching ||
                  !selectedAccount?.value
                }
                variant="primary"
                text="Continuar"
                onPress={() => {
                  //To test NEEDS_ONBOARDING
                  // dispatch(updateAction("NEEDS_ONBOARDING"));
                  if (
                    assessment.required_action === "NEEDS_ONBOARDING" ||
                    assessment.failed_reason === "ALLARIAMAS_ACCOUNT_NOT_EXISTS"
                  ) {
                    dispatch(updateAction("NEEDS_ONBOARDING"));
                  } else if (
                    [
                      "CREATE_ACCOUNT_OWNER_FOR_ALL_COOWNERS",
                      "CREATE_ACCOUNT_WITH_THIS_EMAIL_AS_OWNER",
                    ].includes(assessment.required_action)
                  ) {
                    dispatch(updateAction("CREATE_ACCOUNT"));
                  } else dispatch(updateAction("ALLARIAMAS_SELECTOR"));
                }}
                disabled={
                  assessmentAccountsLoading ||
                  assessmentAccountsFetching ||
                  !selectedAccount?.value
                }
                className={isMobile ? "w-full" : ""}
              />
            </div>
          </ModalFooter>
        </>
      )}
    </>
  );
};

export default AccountSelector;
